// @flow

import React, {useState, useCallback, useMemo} from 'react';

import {makeStyles} from '@material-ui/styles';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';

import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';

import {colors} from '../../../../../assets/styles/colors';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';

import ModalSelectItems from '../../../../../elements/ModalSelectItems/ModalSelectItems';
import {getImageUrl} from '../../../../../utils/image_provider';
import {useIsFeatureInTier} from '../../../../../utils/component';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';

import {actions as ManualOrderActions} from '../../../../manual-order/duck';
import {actions as CategoryActions} from '../../../../categories/duck';
import {actions as SubcategoryActions} from '../../../../sub-categories/duck';

import {useTypedSelector} from '../../../../../store';
import {useBindedActions} from '../../../../../utils/globalStore';
import {useProductFormContext} from '../../../context/ProductForm';

import type {Theme} from '@material-ui/core';

const actions = {
  ...ManualOrderActions,
  ...CategoryActions,
  ...SubcategoryActions,
};

const useStyles = makeStyles((theme: Theme) => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  sectionSubtitle: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  infoText: {
    color: colors.grey900,
    fontSize: 14,
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectProducts: {
    height: 48,
    width: '100%',
    borderRadius: 8,
    '& span': {
      borderRadius: 8,
    },
  },
  wrapSelect: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  contentList: {
    borderRadius: '12px',
    marginTop: theme.spacing(1),
    background: colors.grey100,
  },
  listItemsWrapper: {
    overflow: 'auto',
    padding: '16px',
    maxHeight: '530px',
  },
  deleteItems: {
    color: '#E13065',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowItem: {
    width: '100%',
    display: 'flex',
  },
  imageItem: {
    borderRadius: '8px',
  },
  wrapItemName: {
    width: '100%',
    margin: theme.spacing(0, 1, 0, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameItem: {
    width: '100%',
    textAlign: 'left',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    color: colors.grey800,
  },
  deleteButton: {
    color: '#E54D7B',
  },
  wrapperItem: {
    width: '100%',
  },
}));

type Props = {
  disabled: boolean,
};

export default function RelatedProducts(props: Props) {
  const {disabled} = props;

  const {
    enabledRelatedProducts,
    setEnabledRelatedProducts,
    relatedProductsOrigin,
    setRelatedProductsOrigin,
    listItems,
    setListItems,
    listProductsItems,
    setListProductsItems,
    listCategoryItems,
    setListCategoryItems,
    listSubcategoriesItems,
    setListSubcategoriesItems,
  } = useProductFormContext();
  const classes = useStyles();

  const [openModal, setOpenModal] = useState(false);
  const hasRelatedProductFeature = useIsFeatureInTier('RELATED_PRODUCTS');

  const {
    fetchCategoriesSearch,
    fetchProducts,
    fetchSubcategories,
    resetProductsFilter,
    resetCategoriesFilter,
    resetSubcategoriesFilter,
  } = useBindedActions(actions);

  const productsFilter = useTypedSelector(state => state.manualOrder.products);
  const paginationProducts = useTypedSelector(
    state => state.manualOrder.pagination
  );
  const categoriesFilter = useTypedSelector(
    state => state.categories.categoriesFilter
  );
  const isFetchingProducts = useTypedSelector(
    state => state.manualOrder.isFetchingProducts
  );
  const isFetchingCategories = useTypedSelector(
    state => state.categories.isFetching
  );
  const subcategoriesFilter = useTypedSelector(
    state => state.subCategories.subcategories
  );
  const isFetchingSubcategories = useTypedSelector(
    state => state.subCategories.isFetchingSubcategories
  );
  const paginationCategories = useTypedSelector(
    state => state.categories.paginationFilter
  );
  const paginationSubcategories = useTypedSelector(
    state => state.subCategories.pagination
  );

  const handleEnabledRelatedProducts = () => {
    setEnabledRelatedProducts(ps => !ps);
  };

  const modalTitle = {
    PRODUCT: 'Agregar productos similares',
    PRODUCT_CATEGORY: 'Seleccionar categorías',
    PRODUCT_SUB_CATEGORY: 'Seleccionar subcategorías',
  }[relatedProductsOrigin];

  const itemsSelected = {
    PRODUCT: productsFilter,
    PRODUCT_CATEGORY: categoriesFilter,
    PRODUCT_SUB_CATEGORY: subcategoriesFilter,
  }[relatedProductsOrigin];

  const paginationSelected = {
    PRODUCT: paginationProducts,
    PRODUCT_CATEGORY: paginationCategories,
    PRODUCT_SUB_CATEGORY: paginationSubcategories,
  }[relatedProductsOrigin];

  const isFetchingItemsSelected = {
    PRODUCT: isFetchingProducts,
    PRODUCT_CATEGORY: isFetchingCategories,
    PRODUCT_SUB_CATEGORY: isFetchingSubcategories,
  }[relatedProductsOrigin];

  const resetListFilterSelected = {
    PRODUCT: resetProductsFilter,
    PRODUCT_CATEGORY: resetCategoriesFilter,
    PRODUCT_SUB_CATEGORY: resetSubcategoriesFilter,
  }[relatedProductsOrigin];

  const buildCustomQueryParamsCallback = useCallback(() => {
    let params = {};
    if (relatedProductsOrigin === 'PRODUCT') {
      params = {
        enabled: 1,
        size: 8,
      };
    } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
      params = {
        size: 8,
      };
    } else {
      params = {
        size: 8,
      };
    }
    return params;
  }, [relatedProductsOrigin]);

  const customFetchItemsCallback = useCallback(
    (queryFilter: {[key: string]: any}, addPage?: boolean = false) => {
      if (queryFilter) {
        if (relatedProductsOrigin === 'PRODUCT') {
          fetchProducts({queryFilter, addPage});
        } else if (relatedProductsOrigin === 'PRODUCT_CATEGORY') {
          fetchCategoriesSearch({queryFilter, addPage});
        } else {
          fetchSubcategories({queryFilter, addPage});
        }
      }
    },
    [
      fetchCategoriesSearch,
      fetchProducts,
      fetchSubcategories,
      relatedProductsOrigin,
    ]
  );

  const listCustomItems = useMemo(() => {
    return {
      PRODUCT: listProductsItems,
      PRODUCT_CATEGORY: listCategoryItems,
      PRODUCT_SUB_CATEGORY: listSubcategoriesItems,
    };
  }, [listCategoryItems, listProductsItems, listSubcategoriesItems]);

  const setterCustomItems = useMemo(() => {
    return {
      PRODUCT: setListProductsItems,
      PRODUCT_CATEGORY: setListCategoryItems,
      PRODUCT_SUB_CATEGORY: setListSubcategoriesItems,
    };
  }, [setListCategoryItems, setListProductsItems, setListSubcategoriesItems]);

  const currentListItems = useMemo(() => {
    switch (relatedProductsOrigin) {
      case 'PRODUCT':
        return listCustomItems.PRODUCT;
      case 'PRODUCT_CATEGORY':
        return listCustomItems.PRODUCT_CATEGORY;
      case 'PRODUCT_SUB_CATEGORY':
        return listCustomItems.PRODUCT_SUB_CATEGORY;
      default:
        return [];
    }
  }, [listCustomItems, relatedProductsOrigin]);

  const currentSetItems: any = useMemo(() => {
    switch (relatedProductsOrigin) {
      case 'PRODUCT':
        return setterCustomItems.PRODUCT;
      case 'PRODUCT_CATEGORY':
        return setterCustomItems.PRODUCT_CATEGORY;
      case 'PRODUCT_SUB_CATEGORY':
        return setterCustomItems.PRODUCT_SUB_CATEGORY;
      default:
        return () => null;
    }
  }, [relatedProductsOrigin, setterCustomItems]);

  const handleSetListItems = useCallback(
    value => {
      setListItems(value);
      currentSetItems(value);
    },
    [currentSetItems, setListItems]
  );

  const onSelectChange = e => {
    currentSetItems(listItems);
    setListItems(listCustomItems[e.target.value] || []);
    setRelatedProductsOrigin(e.target.value);
  };

  const handleDeleteListItem = (id: string) => {
    const newList: Object[] = listItems.filter(el => el.id !== id);
    currentSetItems(prev => prev.filter(el => el.id !== id));
    setListItems(newList);
  };

  const buildRowItem = (item: any) => {
    let image = item.mainImageId || item.portraitImageId;

    if (item.mainImage) image = item.mainImage.id;

    return (
      <div className={classes.item}>
        <div className={classes.rowItem}>
          <img
            className={classes.imageItem}
            alt={item.name}
            src={getImageUrl(image)}
            width="56"
            height="56"
          />
          <div className={classes.wrapItemName}>
            <div className={classes.nameItem}>{item.name}</div>
          </div>
        </div>
        <div>
          <IconButton onClick={() => handleDeleteListItem(item.id)}>
            <DeleteOutlinedIcon className={classes.deleteButton} />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <section className={classes.contentSection}>
      <div>
        <Typography className={classes.sectionTitle}>
          Productos Similares{' '}
          {!hasRelatedProductFeature && <PremiumFeatureBadge />}
        </Typography>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                disabled={!hasRelatedProductFeature}
                checked={enabledRelatedProducts}
                onChange={handleEnabledRelatedProducts}
                name="checkedI"
                color="primary"
              />
            }
            className={classes.sectionCheckbox}
            label="Configura los productos similares que quieres que tus compradores vean."
          />
        </div>
      </div>

      {enabledRelatedProducts && (
        <>
          <Divider className={classes.divider} />
          <div>
            <Typography className={classes.sectionSubtitle}>
              Selecciona el origen de los productos similares
            </Typography>
            <div className={classes.wrapSelect}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    // classes={{root: classes.bigSelect}}
                    native
                    fullWidth
                    value={relatedProductsOrigin}
                    onChange={onSelectChange}
                    disabled={disabled}
                  >
                    <option value="" hidden />
                    <option value="PRODUCT">Productos específicos</option>
                    <option value="PRODUCT_CATEGORY">
                      Categorias específicas
                    </option>
                    <option value="PRODUCT_SUB_CATEGORY">
                      Subcategorias específicas
                    </option>
                  </CustomSelect>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomButton
                    fullWidth
                    variant="outlined"
                    className={classes.selectProducts}
                    onClick={() => setOpenModal(true)}
                    disabled={currentListItems.length >= 5}
                  >
                    Seleccionar{' '}
                    {
                      {
                        PRODUCT: 'Productos',
                        PRODUCT_CATEGORY: 'Categorías',
                        PRODUCT_SUB_CATEGORY: 'Subcategorías',
                      }[relatedProductsOrigin]
                    }
                  </CustomButton>
                </Grid>
              </Grid>
            </div>
            <Typography
              className={classes.sectionSubtitle}
              style={{marginTop: 8}}
            >
              {
                {
                  PRODUCT: 'Productos',
                  PRODUCT_CATEGORY: 'Categorías',
                  PRODUCT_SUB_CATEGORY: 'Subcategorías',
                }[relatedProductsOrigin]
              }{' '}
              seleccionados ({listItems.length}/5)
            </Typography>
            {listItems.length > 0 && (
              <div className={classes.contentList}>
                <Grid
                  container
                  spacing={1}
                  className={classes.listItemsWrapper}
                >
                  {listItems.map((p, index: number) => (
                    <Grid item xs={12} key={index} className={classes.item}>
                      <div className={classes.wrapperItem}>
                        {buildRowItem(p)}
                        {index < listItems.length - 1 && (
                          <Divider className={classes.divider} />
                        )}
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </div>
        </>
      )}
      <ModalSelectItems
        maxItems={5}
        title={modalTitle}
        open={openModal}
        handleClose={() => setOpenModal(false)}
        items={itemsSelected}
        type={relatedProductsOrigin}
        pagination={paginationSelected}
        isFetchingItems={isFetchingItemsSelected}
        setListItems={handleSetListItems}
        listItems={listItems}
        resetListFilter={resetListFilterSelected}
        customFetchItemsCallback={customFetchItemsCallback}
        buildCustomQueryParamsCallback={buildCustomQueryParamsCallback}
      />
    </section>
  );
}
